import { useState, useEffect, useCallback } from 'react'
import { message } from 'antd'

export const useAllergies = ({
    patientId,
    apiClasses
}) => {
    const [allergySearchTerm, setAllergySearchTerm] = useState('')
    const [allergySearchResults, setAllergySearchResults] = useState([])
    const [allergySearchResultsLoading, setAllergySearchResultsLoading] = useState(false)
    const [newAllergyQueue, setNewAllergyQueue] = useState([])
    const [patientAllergies, setPatientAllergies] = useState([])
    const [patientAllergiesLoading, setPatientAllergiesLoading] = useState(false)
    const [allergyAdditionErrors, setAllergyAdditionErrors] = useState([])

    const transformCodeType = (codeType) => {
        const codeTypeMap = {
            'ScreenableIngredient': 1,
            'Drug': 2,
            'Ingredient': 3
        }
        return codeTypeMap[codeType] || 1 
    }

    const searchAllergies = useCallback(async (query) => {
        if (!query || query.length < 3) {
            setAllergySearchResults([])
            return
        }

        setAllergySearchResultsLoading(true)
        
        try {
            const response = await apiClasses.PrescriptionsAPI.searchAllergies(query)
            setAllergySearchResults(response.data.allergies?.data || [])
        } catch (error) {
            console.error('Error searching allergies:', error)
            message.error('Error searching allergies')
        } finally {
            setAllergySearchResultsLoading(false)
        }
    }, [apiClasses])

    const addAllergyToQueue = useCallback((allergy) => {
        const transformedAllergy = {
            ...allergy,
            codeType: transformCodeType(allergy.codeType),
            displayName: allergy.displayName || allergy.name,
            name: allergy.name || allergy.displayName
        }
        
        setNewAllergyQueue(prev => {
            const existsInNewQueue = prev.some(item => {
                const itemName = (item.name || '').toLowerCase().trim()
                const itemDisplayName = (item.displayName || '').toLowerCase().trim()
                const newName = (transformedAllergy.name || '').toLowerCase().trim()
                const newDisplayName = (transformedAllergy.displayName || '').toLowerCase().trim()
                
                const exactMatch = itemName === newName || 
                                  itemDisplayName === newDisplayName ||
                                  itemName === newDisplayName ||
                                  itemDisplayName === newName
                
                if (exactMatch) return true
                
                const itemBaseName = (itemDisplayName || itemName)
                    .replace(/\s+(allergy|adverse\s+reaction)$/i, '')
                    .trim()
                const newBaseName = (newDisplayName || newName)
                    .replace(/\s+(allergy|adverse\s+reaction)$/i, '')
                    .trim()
                
                return itemBaseName === newBaseName && itemBaseName.length > 0
            })
            
            if (existsInNewQueue) {
                message.warning('Related allergy or adverse reaction already in queue')
                return prev
            }
            
            return [...prev, transformedAllergy]
        })
    }, [])

    const removeAllergyFromQueue = useCallback((allergyName) => {
        const searchName = (allergyName || '').toLowerCase().trim()
        
        setNewAllergyQueue(prev => prev.filter(item => {
            const itemName = (item.name || '').toLowerCase().trim()
            const itemDisplayName = (item.displayName || '').toLowerCase().trim()
            
            return itemName !== searchName && itemDisplayName !== searchName
        }))
    }, [])

    const resetAllergies = useCallback(() => {
        setNewAllergyQueue([])
        setAllergySearchTerm('')
        setAllergySearchResults([])
        setAllergyAdditionErrors([])
    }, [])

    const getPatientAllergies = useCallback(async () => {
        if (!patientId) return

        setPatientAllergiesLoading(true)
        
        try {
            const response = await apiClasses.PrescriptionsAPI.getPatientAllergies(patientId)
            const allergies = response.data.allergies?.data || []
            setPatientAllergies(allergies)
        } catch (error) {
            console.error('Error fetching patient allergies:', error)
            message.error('Error fetching patient allergies')
        } finally {
            setPatientAllergiesLoading(false)
        }
    }, [patientId, apiClasses])

    useEffect(() => {
        if (allergySearchTerm) {
            const timeoutId = setTimeout(() => {
                searchAllergies(allergySearchTerm)
            }, 300)

            return () => clearTimeout(timeoutId)
        } else {
            setAllergySearchResults([])
        }
    }, [allergySearchTerm, searchAllergies])

    return {
        allergySearchTerm,
        setAllergySearchTerm,
        allergySearchResults,
        allergySearchResultsLoading,
        newAllergyQueue,
        addAllergyToQueue,
        removeAllergyFromQueue,
        resetAllergies,
        patientAllergies,
        patientAllergiesLoading,
        allergyAdditionErrors,
        getPatientAllergies
    }
}
